import { toChartTimestamp } from '../timezone.js'
import { isUsEquityTradingDay } from '../marketSession.js'

const easternDate = new Intl.DateTimeFormat('en-CA', {
  timeZone: 'America/New_York', year: 'numeric', month: '2-digit', day: '2-digit',
})

export function marketDate(now = new Date()) {
  const parts = easternDate.formatToParts(now)
  const get = key => parts.find(p => p.type === key).value
  return `${get('year')}-${get('month')}-${get('day')}`
}

export function normalizeEarningsDates(payload) {
  if (!Array.isArray(payload?.data)) throw new Error('Invalid earnings response')
  const dates = new Map()
  for (const row of payload.data) {
    if (typeof row?.report_date !== 'string' || !/^\d{4}-\d{2}-\d{2}(?:T00:00:00(?:\.000)?Z)?$/.test(row.report_date)) continue
    const date = row.report_date.slice(0, 10)
    const millis = Date.parse(`${date}T00:00:00Z`)
    if (!Number.isFinite(millis) || new Date(millis).toISOString().slice(0, 10) !== date) continue
    const event = {
      date,
      session: ['premarket', 'postmarket'].includes(row.report_time) ? row.report_time : 'unknown',
      source: ['company', 'estimation'].includes(row.source) ? row.source : 'unknown',
      reported: ['string', 'number'].includes(typeof row.actual_eps)
        && String(row.actual_eps).trim() !== '' && Number.isFinite(Number(row.actual_eps)),
    }
    const previous = dates.get(date)
    if (!previous || (event.source === 'company' && previous.source !== 'company')) dates.set(date, event)
    else if (previous.source === event.source) {
      if (previous.session !== event.session) previous.session = 'unknown'
      previous.reported ||= event.reported
    }
  }
  if (payload.data.length && !dates.size) throw new Error('No valid earnings dates')
  return [...dates.values()].sort((a, b) => a.date.localeCompare(b.date))
}

export function nextEarnings(events, now = new Date()) {
  const today = marketDate(now)
  return events.find(event => event.date >= today) || null
}

const labelDateFormatter = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' })
const shortDateFormatter = new Intl.DateTimeFormat('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' })

export function earningsDateLabel(event) {
  return labelDateFormatter.format(new Date(`${event.date}T12:00:00Z`))
}

export function earningsLabel(event) {
  const date = earningsDateLabel(event)
  const session = { premarket: 'Before open', postmarket: 'After close', unknown: '' }[event.session]
  return `${date}${session ? ` · ${session}` : ''}${event.source === 'estimation' ? ' · Estimated' : ''}`
}

// These are date annotations, not exact release timestamps or reaction bars.
// Do not attach a future/off-history event to the last available candle.
export function earningsBarTime(event, bars, timeframe, timezone) {
  const midnight = Date.parse(`${event.date}T00:00:00Z`) / 1000
  if (timeframe === 'daily') return bars.find(bar => bar.time === midnight)?.time ?? null
  if (timeframe === 'weekly') {
    const weekday = new Date(midnight * 1000).getUTCDay()
    const monday = midnight - ((weekday + 6) % 7) * 86400
    return bars.find(bar => bar.time >= monday && bar.time < monday + 7 * 86400)?.time ?? null
  }
  const day = event.date.replaceAll('-', '')
  const start = toChartTimestamp(`${day} 00:00:00 US/Eastern`, timezone)
  const end = toChartTimestamp(`${day} 23:59:59 US/Eastern`, timezone)
  const sessionBars = bars.filter(bar => bar.time >= start && bar.time <= end)
  return (event.session === 'postmarket' ? sessionBars.at(-1) : sessionBars[0])?.time ?? null
}

// Project only date-based bars. Intraday bars can be sparse and session-specific;
// only actual session bars are used instead of inventing intraday slots.
// Use the chart's last actual index, not the candle array index (other series may
// contribute time points). No whitespace series or viewport changes are needed.
export function earningsFutureOffset(event, lastTime, timeframe, tradingCalendar) {
  if (!Number.isFinite(lastTime) || !['daily', 'weekly'].includes(timeframe)) return null
  const target = Date.parse(`${event.date}T00:00:00Z`) / 1000
  const days = (target - lastTime) / 86400
  if (days <= 0 || days > 370) return null
  if (timeframe === 'weekly') {
    const monday = time => time - ((new Date(time * 1000).getUTCDay() + 6) % 7) * 86400
    return (monday(target) - monday(lastTime)) / (7 * 86400)
  }
  // A non-session report date has no daily slot; do not move it onto
  // another session's candle.
  if (!isUsEquityTradingDay(new Date((target + 12 * 3600) * 1000), tradingCalendar)) return null
  let count = 0
  for (let time = lastTime + 86400; time <= target; time += 86400) {
    if (isUsEquityTradingDay(new Date((time + 12 * 3600) * 1000), tradingCalendar)) count++
  }
  return count
}

export function earningsShortDate(event) {
  return shortDateFormatter.format(new Date(`${event.date}T12:00:00Z`))
}
